import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config/constants';
import { buildAuthHeaders, isTokenExpired } from '../utils/auth';

interface Book {
  id: number;
  title: string;
  author: string;
  coverImage?: string;
  description?: string;
  category?: string;
  publishedYear?: number;
  fileName?: string;
  fileType?: string;
}

interface Favorite {
  id: number;
  userId: number;
  bookId: number;
  createdAt: string;
  Book: Book;
}

interface FavoritesContextType {
  favorites: Favorite[];
  loading: boolean;
  addToFavorites: (bookId: number) => Promise<boolean>;
  removeFromFavorites: (bookId: number) => Promise<boolean>;
  isFavorite: (bookId: number) => boolean;
  toggleFavorite: (bookId: number) => Promise<boolean>;
  fetchFavorites: () => Promise<void>;
}

const FavoritesContext = createContext<FavoritesContextType | undefined>(undefined);

export const useFavorites = (): FavoritesContextType => {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
};

interface FavoritesProviderProps {
  children: ReactNode;
}

export const FavoritesProvider: React.FC<FavoritesProviderProps> = ({ children }) => {
  const [favorites, setFavorites] = useState<Favorite[]>([]);
  const [loading, setLoading] = useState(false);
  const { user, token, logout } = useAuth();

  // Preia lista de favorite de la server
  const fetchFavorites = async () => {
    if (!user || !token || isTokenExpired(token)) {
      if (token && isTokenExpired(token)) {
        logout();
      }
      setFavorites([]);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/favorites`, {
        headers: buildAuthHeaders(token)
      });

      if (response.ok) {
        const data = await response.json();
        setFavorites(data || []);
      } else if (response.status === 401) {
        logout();
        setFavorites([]);
      } else {
        setFavorites([]);
      }
    } catch (error) {
      setFavorites([]);
    } finally {
      setLoading(false);
    }
  };

  // Adaugă o carte la favorite
  const addToFavorites = async (bookId: number): Promise<boolean> => {
    if (!user || !token || isTokenExpired(token)) {
      if (token && isTokenExpired(token)) {
        logout();
      }
      return false;
    }

    try {
      const response = await fetch(`${API_URL}/favorites/${bookId}`, {
        method: 'POST',
        headers: buildAuthHeaders(token, {
          'Content-Type': 'application/json'
        })
      });

      if (response.ok) {
        await fetchFavorites();
        return true;
      } else if (response.status === 401) {
        logout();
      } else {
        return false;
      }
    } catch (error) {
      return false;
    }
    return false;
  };

  // Elimină o carte din favorite
  const removeFromFavorites = async (bookId: number): Promise<boolean> => {
    if (!user || !token || isTokenExpired(token)) {
      if (token && isTokenExpired(token)) {
        logout();
      }
      return false;
    }

    try {
      const response = await fetch(`${API_URL}/favorites/${bookId}`, {
        method: 'DELETE',
        headers: buildAuthHeaders(token, {
          'Content-Type': 'application/json'
        })
      });

      if (response.ok) {
        setFavorites(prev => prev.filter(fav => fav.bookId !== bookId));
        return true;
      } else if (response.status === 401) {
        logout();
      } else {
        return false;
      }
    } catch (error) {
      return false;
    }
    return false;
  };

  // Verifică dacă o carte este în favorite
  const isFavorite = (bookId: number): boolean => {
    return favorites.some(fav => fav.bookId === bookId);
  };

  // Toggle status favorite
  const toggleFavorite = async (bookId: number): Promise<boolean> => {
    const currentlyFavorite = isFavorite(bookId);
    
    if (currentlyFavorite) {
      return await removeFromFavorites(bookId);
    } else {
      return await addToFavorites(bookId);
    }
  };

  useEffect(() => {
    if (user && token) {
      fetchFavorites();
    } else {
      setFavorites([]);
    }
  }, [user, token]);

  const value: FavoritesContextType = {
    favorites,
    loading,
    addToFavorites,
    removeFromFavorites,
    isFavorite,
    toggleFavorite,
    fetchFavorites
  };

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
}; 
